/**
 * Agent tools (ARCHITECTURE.md §6, §8).
 *
 * One card per tool-bearing agent — the strategy-builder chat and the advisor
 * gate — each listing its full tool catalog (names/tiers/core-ness straight from
 * the server, whose SSOT is the `@richrich/ai` registries) with an enable toggle.
 * Core tools render locked: the server rejects disabling them and the UI never
 * offers it. Saving replaces that agent's disabled-set; a change applies from the
 * operator's next chat turn / the advisor's next gated decision.
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@richrich/ui";
import { useI18n } from "@richrich/i18n/react";
import type { MessageKey } from "@richrich/i18n";
import type { AgentId, AgentToolInfo, PutAgentTools } from "@richrich/contracts";
import { PageShell } from "../../components/PageShell";
import { agentToolsQuery } from "../../features/settings/api";
import { usePutAgentTools } from "../../features/settings/hooks";
import { AGENT_TOOL_DESC_KEYS } from "../../features/settings/catalog";
import { Button, ErrorNote, SectionCard, SettingRow, Toggle } from "../../components/ui/controls";

export const Route = createFileRoute("/settings/tools")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(agentToolsQuery);
  },
  component: AgentToolsPage,
});

/** Static per-agent presentation (copy keys only; the tool list is server data). */
const AGENT_CARDS: readonly { agent: AgentId; titleKey: MessageKey; descKey: MessageKey }[] = [
  {
    agent: "chat",
    titleKey: "settings.toolsChatTitle",
    descKey: "settings.toolsChatDescription",
  },
  {
    agent: "advisor",
    titleKey: "settings.toolsAdvisorTitle",
    descKey: "settings.toolsAdvisorDescription",
  },
];

function AgentToolsPage() {
  const { t } = useI18n();
  const tools = useQuery(agentToolsQuery);

  return (
    <PageShell
      eyebrow={t("settings.toolsEyebrow")}
      title={t("settings.toolsTitle")}
      description={t("settings.toolsDescription")}
      loading={tools.isLoading}
    >
      <div className="flex flex-col gap-6">
        {AGENT_CARDS.map((card) => (
          <AgentToolsCard
            key={card.agent}
            agent={card.agent}
            title={t(card.titleKey)}
            description={t(card.descKey)}
            tools={tools.data?.[card.agent] ?? []}
          />
        ))}
      </div>
    </PageShell>
  );
}

function AgentToolsCard({
  agent,
  title,
  description,
  tools,
}: {
  agent: AgentId;
  title: string;
  description: string;
  tools: readonly AgentToolInfo[];
}) {
  const { t } = useI18n();
  const put = usePutAgentTools();
  // Local overrides on top of the server state; `null`/absent ⇒ server value. The
  // draft is cleared on save success (the mutation replaces the query data, so the
  // server state IS the draft at that point).
  const [draft, setDraft] = useState<Readonly<Record<string, boolean>>>({});

  const enabledOf = (tool: AgentToolInfo): boolean => draft[tool.name] ?? tool.enabled;
  const dirty = tools.some((tool) => enabledOf(tool) !== tool.enabled);

  function save() {
    const disabled = tools.filter((tool) => !enabledOf(tool)).map((tool) => tool.name);
    const body: PutAgentTools =
      agent === "chat" ? { chat: { disabled } } : { advisor: { disabled } };
    put.mutate(body, { onSuccess: () => setDraft({}) });
  }

  return (
    <SectionCard title={title} description={description}>
      <div className="flex flex-col divide-y divide-hairline">
        {tools.map((tool) => (
          <SettingRow
            key={tool.name}
            label={
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[13px]">{tool.name}</span>
                {tool.core ? <Badge tone="info">{t("settings.toolCoreBadge")}</Badge> : null}
                {tool.tier === "lazy_load" ? (
                  <Badge tone="neutral">{t("settings.toolLazyBadge")}</Badge>
                ) : null}
              </span>
            }
            description={
              tool.core
                ? t("settings.toolCoreHint")
                : AGENT_TOOL_DESC_KEYS[tool.name] !== undefined
                  ? t(AGENT_TOOL_DESC_KEYS[tool.name] as MessageKey)
                  : undefined
            }
            control={
              <Toggle
                checked={enabledOf(tool)}
                disabled={tool.core || put.isPending}
                label={tool.name}
                onCheckedChange={(next) => setDraft((d) => ({ ...d, [tool.name]: next }))}
              />
            }
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-4">
        <Button variant="primary" pending={put.isPending} disabled={!dirty} onClick={save}>
          {t("settings.saveTools")}
        </Button>
        {dirty ? (
          <span className="text-xs text-muted">{t("settings.toolsUnsavedHint")}</span>
        ) : null}
        {put.isError ? <ErrorNote>{t("settings.saveToolsFailed")}</ErrorNote> : null}
      </div>
    </SectionCard>
  );
}
